'use client'

/**
 * League table for /data/brand-index — brand-standard treatment matching the
 * price-index and energy-index tables (white card, gray-50 uppercase header,
 * hairline dividers, right-aligned tabular numerals, overflow-x scroll
 * container), click-to-sort via the shared SortableTable.
 *
 * Every value arrives as a plain row from app/data/brand-index/page.tsx, which
 * owns the single importer of app/data/_data/brand-index.json — sorting only
 * reorders, it never derives. A null figure renders as an em dash: absent,
 * never estimated.
 */
import { SortableTable, type SortableColumn } from '../_components/sortable-table'
import { EntityLink, type LinkTarget } from '../_components/entity-link'
import type { BrandRow } from './brand-index-summary'

/**
 * A league-table row plus the live destination the page resolved for its
 * brand (FRIDGE-334): the /brands/<slug> page when it serves 200, else the
 * brand's indexed collection, else null → the name renders as plain text.
 * Resolved server-side by lib/data/entity-links.ts; the table only renders.
 */
export type LinkedBrandRow = BrandRow & { link?: LinkTarget }

function fmtInt(n: number): string {
  return n.toLocaleString('en-US')
}

function fmtPrice(usd: number | null): string {
  return usd === null ? '—' : '$' + usd.toLocaleString('en-US')
}

export function BrandLeagueTable({ rows }: { rows: LinkedBrandRow[] }) {
  const columns: SortableColumn<LinkedBrandRow>[] = [
    {
      key: 'brand',
      header: 'Brand',
      sortValue: (r) => r.brand,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.brand}
        </EntityLink>
      ),
    },
    {
      key: 'n',
      header: 'Models',
      numeric: true,
      sortValue: (r) => r.n,
      render: (r) => <span className="font-bold">{fmtInt(r.n)}</span>,
    },
    {
      key: 'median_price',
      header: 'Median price',
      numeric: true,
      sortValue: (r) => r.median_price ?? -1,
      render: (r) => <span className="font-semibold">{fmtPrice(r.median_price)}</span>,
    },
    {
      key: 'range',
      header: 'P10–P90 price',
      numeric: true,
      sortValue: (r) => (r.p90_price ?? 0) - (r.p10_price ?? 0),
      render: (r) =>
        r.p10_price === null || r.p90_price === null ? (
          '—'
        ) : (
          <span className="text-gray-500">
            {fmtPrice(r.p10_price)}–{fmtPrice(r.p90_price)}
          </span>
        ),
    },
    {
      key: 'median_cuft',
      header: 'Median ft³',
      numeric: true,
      sortValue: (r) => r.median_cuft ?? -1,
      render: (r) => (r.median_cuft === null ? '—' : String(r.median_cuft)),
    },
    {
      key: 'types_n',
      header: 'Types',
      numeric: true,
      sortValue: (r) => r.types_n,
      render: (r) => <span className="text-gray-600">{r.types_n}</span>,
    },
    {
      key: 'es_median_kwh',
      header: 'Certified kWh/yr',
      numeric: true,
      sortValue: (r) => r.es_median_kwh ?? -1,
      render: (r) => (r.es_median_kwh === null ? '—' : fmtInt(r.es_median_kwh)),
    },
    {
      key: 'es_n',
      header: 'Certified models',
      numeric: true,
      sortValue: (r) => r.es_n,
      render: (r) => <span className="text-gray-600">{fmtInt(r.es_n)}</span>,
    },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.brand}
      defaultSortKey="n"
      defaultDir="desc"
      minWidth={920}
      caption="Refrigerator Brand Index — every listed brand, sortable | Fridge.com"
    />
  )
}
